import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	AlertTriangle, ArrowLeft, ArrowRight, Copy, Loader2, Share2, Trophy, Users, Wallet,
} from "lucide-react";
import { toast } from "sonner";

import KpiCard from "@/components/common/KpiCard";
import PageHeader from "@/components/common/PageHeader";
import PageSearchInput from "@/components/common/PageSearchInput";
import StatusBadge from "@/components/common/StatusBadge";
import GeneratedAvatar from "@/components/common/GeneratedAvatar";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  deactivateAdminReferralCode,
  generateAdminReferralCode,
  listAdminReferrals,
  updateAdminReferral,
} from "@/lib/api";
import { fmtDate } from "@/lib/formatters";
import { useApp } from "@/context/AppContext";

const PAGE_SIZE = 40;

function money(amount = 0, currency = "NGN") {
	return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount / 100);
}

function moneyMap(values = {}) {
	const entries = Object.entries(values).filter(([, amount]) => amount);
	if (!entries.length) return money(0);
	return entries.map(([currency, amount]) => money(amount, currency)).join(" · ");
}

function ReferralSkeleton() {
  return <div className="space-y-6"><Skeleton className="h-24 w-full" /><div className="grid grid-cols-2 gap-4 md:grid-cols-5">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div><Skeleton className="h-96 rounded-xl" /></div>;
}

function EmptyTableRow({ colSpan, message }) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan} className="p-5">
        <div className="flex min-h-56 items-center justify-center rounded-xl bg-[#F4F9F5] px-6 text-center text-sm text-[#6B7280]">
          {message}
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function Referrals() {
  const queryClient = useQueryClient();
  const { currentAdmin } = useApp();
  const canOperate = currentAdmin?.permissions?.includes("referrals.manage");
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const search = q.trim();
  const [page, setPage] = useState(0);
  const [cursorStack, setCursorStack] = useState([""]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [ownerType, setOwnerType] = useState("partner");
  const [generated, setGenerated] = useState("");
  const [payoutTarget, setPayoutTarget] = useState(null);
  const [payoutReference, setPayoutReference] = useState("");

  useEffect(() => { setPage(0); setCursorStack([""]); }, [search, tab]);
  const filters = useMemo(() => ({
    cursor: cursorStack[page] || "", limit: PAGE_SIZE, query: search,
    view: tab === "fraud" ? "fraud" : "all",
  }), [cursorStack, page, search, tab]);
  const { data, error, isLoading } = useQuery({
    queryKey: ["admin-referrals", filters],
    queryFn: ({ signal }) => listAdminReferrals(filters, signal),
    placeholderData: keepPreviousData,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-referrals"] });
  const action = useMutation({
    mutationFn: ({ id, ...body }) => updateAdminReferral(id, body),
    onSuccess: () => { toast.success("Referral updated"); setPayoutTarget(null); setPayoutReference(""); refresh(); },
    onError: (e) => toast.error(e?.message || "Could not update referral"),
  });
  const generate = useMutation({
    mutationFn: () => generateAdminReferralCode({ name, contact, ownerType }),
    onSuccess: (result) => { setGenerated(result.code.code); toast.success("Secure referral code created"); refresh(); },
    onError: (e) => toast.error(e?.message || "Could not create code"),
  });
  const deactivate = useMutation({
    mutationFn: deactivateAdminReferralCode,
    onSuccess: () => { toast.success("Referral code deactivated"); refresh(); },
    onError: (e) => toast.error(e?.message || "Could not deactivate code"),
  });
  if (isLoading) return <ReferralSkeleton />;

  const metrics = data?.metrics || {};
  const items = data?.items || [];
  const nextPage = () => {
    if (!data?.nextCursor) return;
    setCursorStack((current) => [...current.slice(0, page + 1), data.nextCursor]);
    setPage((value) => value + 1);
  };
  const act = (item, type, reason = "") => action.mutate({ id: item.id, action: type, reason });

  return (
    <div data-testid="referrals-page">
	  <PageHeader eyebrow="Growth" title="Referrals" subtitle="Track verified subscriptions and each ₦5,000 or $10 cashback milestone."
		actions={canOperate ? <div className="flex gap-2">
		  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button variant="outline"><Share2 className="mr-2 h-4 w-4" />Generate code</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Generate referral code</DialogTitle><DialogDescription>Create a cryptographically random code for an approved partner or field agent.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {["partner", "agent"].map((type) => <Button key={type} type="button" variant={ownerType === type ? "default" : "outline"} onClick={() => setOwnerType(type)} className="capitalize">{type}</Button>)}
              </div>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Partner or agent name" maxLength={120} />
              <Input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Contact email or phone (optional)" maxLength={254} />
              {generated && <div className="flex items-center rounded-xl bg-[#F0F6F1] p-3"><span className="flex-1 font-mono text-lg font-bold text-[#064E3B]">{generated}</span><Button variant="ghost" onClick={() => { navigator.clipboard?.writeText(generated); toast.success("Copied"); }}><Copy className="mr-1 h-4 w-4" />Copy</Button></div>}
            </div>
            <DialogFooter><Button disabled={generate.isPending || name.trim().length < 2} onClick={() => generate.mutate()}>{generate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Generate securely</Button></DialogFooter>
          </DialogContent>
          </Dialog>
        </div> : null}
      />

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-5">
        <KpiCard label="Total referrals" value={metrics.total || 0} icon={Share2} accent="#A7F3D0" />
		<KpiCard label="Subscribed" value={metrics.qualified || 0} icon={Trophy} accent="#D9F99D" hint={`${metrics.progressCount || 0}/10 to next reward`} />
		<KpiCard label="Rewards" value={metrics.milestonesEarned || 0} icon={Wallet} accent="#FDE047" />
		<KpiCard label="Paid out" value={data?.financialRestricted ? "Restricted" : moneyMap(metrics.paid)} icon={Wallet} accent="#BAE6FD" />
		<KpiCard label="Pending" value={data?.financialRestricted ? "Restricted" : moneyMap(metrics.pending)} icon={Users} accent="#F5E6D3" hint={!data?.financialRestricted && Object.values(metrics.recoveryRequired || {}).some(Boolean) ? `${moneyMap(metrics.recoveryRequired)} needs recovery` : undefined} />
      </div>

      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error.message}</div>}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto rounded-xl border bg-white p-1">
          <TabsTrigger value="all">All referrals</TabsTrigger><TabsTrigger value="codes">Codes</TabsTrigger><TabsTrigger value="fraud">Fraud checks</TabsTrigger>
        </TabsList>
        <TabsContent value="all" className="mt-5 space-y-4">
          <PageSearchInput className="max-w-md" value={q} onSearch={setQ} placeholder="Search by shop, referrer or code…" />
          <div className="card-elev overflow-hidden rounded-xl">
			<Table><TableHeader><TableRow className="bg-[#F0F6F1]/60"><TableHead>Referred shop</TableHead><TableHead>Referrer</TableHead><TableHead>Code</TableHead><TableHead>Signed up</TableHead><TableHead>Subscribed</TableHead><TableHead>Milestone value</TableHead><TableHead>Reward</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {!items.length ? <EmptyTableRow colSpan={8} message="No referrals match the current filters." /> : items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-semibold text-[#064E3B]">{item.shopName}{item.fraudStatus === "flagged" && <span className="ml-2 text-xs text-red-700"><AlertTriangle className="inline h-3 w-3" /> Flagged</span>}</TableCell>
                    <TableCell><div className="flex items-center gap-2"><GeneratedAvatar seed={item.referrerCode} name={item.referrerName} className="h-8 w-8" /><div><div className="font-medium">{item.referrerName}</div><div className="text-xs capitalize text-muted-foreground">{item.referrerType}</div></div></div></TableCell>
                    <TableCell><span className="rounded-full bg-[#F0F6F1] px-2 py-1 font-mono text-xs">{item.referrerCode}</span></TableCell>
                    <TableCell>{fmtDate(item.signedUpAt)}</TableCell><TableCell>{item.convertedAt ? fmtDate(item.convertedAt) : "—"}</TableCell>
					<TableCell>{item.rewardAmountMinor ? money(item.rewardAmountMinor, item.rewardCurrencyCode) : "—"}</TableCell>
					<TableCell>{item.rewardStatus === "not_earned" ? <span className="text-xs text-[#6B7280]">Building milestone</span> : <StatusBadge status={item.rewardStatus} />}</TableCell>
                    <TableCell className="text-right"><div className="flex justify-end gap-1">
					  {canOperate && item.rewardStatus === "pending" && item.fraudStatus !== "flagged" && <Button size="sm" variant="outline" onClick={() => act(item, "approve")}>Approve</Button>}
                      {canOperate && item.rewardStatus === "approved" && <Button size="sm" onClick={() => setPayoutTarget(item)}>Pay</Button>}
                      {canOperate && !["paid", "rejected"].includes(item.rewardStatus) && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => act(item, "reject", "Rejected by administrator")}>Reject</Button>}
                    </div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {(page > 0 || data?.nextCursor) && <div className="flex items-center justify-between text-xs text-muted-foreground"><span>Page {page + 1} · 40 records per page</span><div className="flex gap-2"><Button size="sm" variant="outline" disabled={!page} onClick={() => setPage((v) => v - 1)}><ArrowLeft className="mr-1 h-4 w-4" />Previous</Button><Button size="sm" variant="outline" disabled={!data?.nextCursor} onClick={nextPage}>Next<ArrowRight className="ml-1 h-4 w-4" /></Button></div></div>}
        </TabsContent>
        <TabsContent value="codes" className="mt-5">
          <div className="card-elev overflow-hidden rounded-xl"><Table><TableHeader><TableRow className="bg-[#F0F6F1]/60"><TableHead>Code owner</TableHead><TableHead>Code</TableHead><TableHead>Type</TableHead><TableHead>Created</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader><TableBody>
            {(data?.codes || []).map((codeItem) => <TableRow key={codeItem.id}><TableCell><div className="font-semibold text-[#064E3B]">{codeItem.ownerName}</div>{codeItem.contact && <div className="text-xs text-muted-foreground">{codeItem.contact}</div>}</TableCell><TableCell><button className="inline-flex items-center gap-2 rounded-full bg-[#F0F6F1] px-3 py-1 font-mono text-xs" onClick={() => { navigator.clipboard?.writeText(codeItem.code); toast.success("Code copied"); }}>{codeItem.code}<Copy className="h-3 w-3" /></button></TableCell><TableCell className="capitalize">{codeItem.ownerType}</TableCell><TableCell>{fmtDate(codeItem.createdAt)}</TableCell><TableCell><StatusBadge status={codeItem.status} /></TableCell><TableCell className="text-right">{canOperate && codeItem.status === "active" && codeItem.ownerType !== "customer" ? <Button size="sm" variant="ghost" className="text-red-600" disabled={deactivate.isPending} onClick={() => deactivate.mutate(codeItem.id)}>Deactivate</Button> : "—"}</TableCell></TableRow>)}
            {!data?.codes?.length && <EmptyTableRow colSpan={6} message="No referral codes have been created." />}
          </TableBody></Table></div>
        </TabsContent>
        <TabsContent value="fraud" className="mt-5 space-y-4">
          <div className="card-elev overflow-hidden rounded-xl">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F0F6F1]/60 hover:bg-[#F0F6F1]/60">
                  <TableHead>Referred shop</TableHead>
                  <TableHead>Referrer</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Risk signal</TableHead>
                  <TableHead>Signed up</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Reward</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!items.length ? (
                  <EmptyTableRow colSpan={8} message="No suspicious referrals require review." />
                ) : items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-semibold text-[#064E3B]">{item.shopName}</TableCell>
                    <TableCell><div className="flex items-center gap-2"><GeneratedAvatar seed={item.referrerCode} name={item.referrerName} className="h-8 w-8" /><div><div className="font-medium text-[#064E3B]">{item.referrerName}</div><div className="text-xs capitalize text-muted-foreground">{item.referrerType}</div></div></div></TableCell>
                    <TableCell><span className="rounded-full bg-[#F0F6F1] px-2.5 py-1 font-mono text-xs text-[#0E5B52]">{item.referrerCode}</span></TableCell>
                    <TableCell><span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700"><AlertTriangle className="h-3 w-3" />{item.fraudReason || "Manual review"}</span></TableCell>
                    <TableCell>{fmtDate(item.signedUpAt)}</TableCell>
                    <TableCell>{item.convertedAt ? "Converted" : "Pre-conversion"}</TableCell>
                    <TableCell>{item.rewardStatus === "not_earned" ? <span className="text-xs text-[#6B7280]">Not earned</span> : <StatusBadge status={item.rewardStatus} />}</TableCell>
                    <TableCell className="text-right">{canOperate ? <div className="flex justify-end gap-1.5"><Button size="sm" variant="outline" disabled={action.isPending} onClick={() => act(item, "clear_fraud", "Reviewed and cleared")}>Clear</Button><Button size="sm" variant="ghost" className="text-red-600" disabled={action.isPending} onClick={() => act(item, "reject", "Fraud review rejected")}>Disqualify</Button></div> : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {(page > 0 || data?.nextCursor) && <div className="flex items-center justify-between text-xs text-muted-foreground"><span>Page {page + 1} · 40 records per page</span><div className="flex gap-2"><Button size="sm" variant="outline" disabled={!page} onClick={() => setPage((value) => value - 1)}><ArrowLeft className="mr-1 h-3.5 w-3.5" />Previous</Button><Button size="sm" variant="outline" disabled={!data?.nextCursor} onClick={nextPage}>Next<ArrowRight className="ml-1 h-3.5 w-3.5" /></Button></div></div>}
        </TabsContent>
      </Tabs>

	  <Dialog open={Boolean(payoutTarget)} onOpenChange={(open) => !open && setPayoutTarget(null)}><DialogContent><DialogHeader><DialogTitle>Record cashback payout</DialogTitle><DialogDescription>Confirm the external {payoutTarget ? money(payoutTarget.rewardAmountMinor, payoutTarget.rewardCurrencyCode) : "cashback"} transfer using its unique bank or payout reference.</DialogDescription></DialogHeader><Input value={payoutReference} onChange={(e) => setPayoutReference(e.target.value)} placeholder="Bank transfer or payout reference" maxLength={120} /><DialogFooter><Button disabled={!payoutReference.trim() || action.isPending} onClick={() => action.mutate({ id: payoutTarget.id, action: "pay", payoutReference })}>{action.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Mark paid</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}
